/**
 * The timer's chime (timer-alerts spec, Petr 2026-10-31): made in the browser, no sound file. Time's up is a soft rise
 * of three notes; a "Still studying?" two quieter ones, so it's told apart by ear.
 *
 * Browsers only let a page make sound after the student has clicked in it, so the sound engine is made and woken on
 * the first click or key press (`primeChime`), then put back to sleep until a chime plays.
 */

export type ChimeKind = 'timesUp' | 'checkIn';

const NOTES: Record<ChimeKind, { frequency: number; delay: number; peak: number; length: number }[]> = {
  timesUp: [
    { frequency: 523.25, delay: 0, peak: 0.16, length: 0.9 },
    { frequency: 659.25, delay: 0.16, peak: 0.16, length: 0.9 },
    { frequency: 783.99, delay: 0.32, peak: 0.16, length: 1.0 }
  ],
  checkIn: [
    { frequency: 659.25, delay: 0, peak: 0.09, length: 0.7 },
    { frequency: 783.99, delay: 0.2, peak: 0.09, length: 0.8 }
  ]
};

let context: AudioContext | null = null;
let sleepTimer: number | undefined;

const makeContext = () => {
  if (context) return context;
  const Context = window.AudioContext || (window as any).webkitAudioContext;
  if (!Context) return null;
  try {
    context = new Context();
  } catch {
    context = null;
  }
  return context;
};

// Asleep between chimes, so an idle engine doesn't keep the sound card busy.
const sleepSoon = (ms: number) => {
  window.clearTimeout(sleepTimer);
  sleepTimer = window.setTimeout(() => {
    context?.suspend().catch(() => undefined);
  }, ms);
};

/** Wakes the sound engine in a click, so it may play later. */
export const primeChime = () => {
  const ctx = makeContext();
  if (!ctx) return;
  ctx
    .resume()
    .then(() => sleepSoon(1000))
    .catch(() => undefined);
};

/** From a click or key press, until the engine has been allowed to run once. Returns the unsubscribe. */
export const primeChimeOnFirstClick = () => {
  const events = ['pointerdown', 'keydown'];
  const prime = () => {
    primeChime();
    events.forEach(event => window.removeEventListener(event, prime, true));
  };
  events.forEach(event => window.addEventListener(event, prime, true));
  return () => events.forEach(event => window.removeEventListener(event, prime, true));
};

/** Plays the chime; quietly nothing when the browser won't let the page make sound. Resolves whether it played. */
export const playChime = async (kind: ChimeKind) => {
  const ctx = makeContext();
  if (!ctx) return false;
  // Not allowed, the browser leaves resume() waiting for the next click: the chime would come then, out of place.
  await Promise.race([ctx.resume().catch(() => undefined), new Promise(resolve => window.setTimeout(resolve, 300))]);
  if (ctx.state !== 'running') return false;
  const start = ctx.currentTime + 0.05;
  NOTES[kind].forEach(note => {
    const at = start + note.delay;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = note.frequency;
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(note.peak, at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + note.length);
    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start(at);
    oscillator.stop(at + note.length + 0.05);
  });
  sleepSoon(2000);
  return true;
};
